import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import api from "@/lib/axios";
import { SUPER_ADMIN_ROLE } from "./types";
import type { AdminRoleName, RBACState, UserRole } from "./types";

interface RBACContextValue extends RBACState {
  hasPermission: (codename: string) => boolean;
  hasRole: (roleName: AdminRoleName) => boolean;
  hasAnyRole: (roleNames: AdminRoleName[]) => boolean;
  refetch: () => Promise<void>;
}

const defaultState: RBACState = {
  roles: [],
  permissions: [],
  isLoading: true,
  error: null,
};

export const RBACContext = createContext<RBACContextValue>({
  ...defaultState,
  hasPermission: () => false,
  hasRole: () => false,
  hasAnyRole: () => false,
  refetch: async () => {},
});

export function RBACProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<RBACState>(defaultState);

  const fetchRBACData = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const [rolesRes, permissionsRes] = await Promise.allSettled([
        api.get<UserRole[]>("/rbac/me/roles/"),
        api.get<{ permissions: string[] }>("/rbac/me/permissions/"),
      ]);

      setState({
        roles: rolesRes.status === "fulfilled" ? rolesRes.value : [],
        permissions:
          permissionsRes.status === "fulfilled"
            ? permissionsRes.value.permissions
            : [],
        isLoading: false,
        error: null,
      });
    } catch {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: "Failed to load RBAC data",
      }));
    }
  }, []);

  useEffect(() => {
    fetchRBACData();
  }, [fetchRBACData]);

  const hasPermission = useCallback(
    (codename: string) => {
      // Super admin has all permissions
      if (state.roles.some((r) => r.role_name === SUPER_ADMIN_ROLE)) return true;
      return state.permissions.includes(codename);
    },
    [state.roles, state.permissions],
  );

  const hasRole = useCallback(
    (roleName: AdminRoleName) => {
      return state.roles.some((r) => r.role_name === roleName && r.is_active);
    },
    [state.roles],
  );

  const hasAnyRole = useCallback(
    (roleNames: AdminRoleName[]) => {
      return state.roles.some(
        (r) => roleNames.includes(r.role_name) && r.is_active,
      );
    },
    [state.roles],
  );

  const value = useMemo(
    () => ({
      ...state,
      hasPermission,
      hasRole,
      hasAnyRole,
      refetch: fetchRBACData,
    }),
    [state, hasPermission, hasRole, hasAnyRole, fetchRBACData],
  );

  return <RBACContext.Provider value={value}>{children}</RBACContext.Provider>;
}
